import { useCallback, useState } from 'react';

/**
 * useState that survives a reload. The visitor's theme and view choice are
 * preferences, not session state — losing them on F5 reads as a bug.
 * localStorage can throw (private mode, quota), so every access is guarded and
 * the page keeps working without persistence.
 */
export const usePersistentState = <T,>(
  key: string,
  fallback: () => T,
  isValid: (value: unknown) => value is T
): [T, (value: T) => void] => {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = window.localStorage.getItem(key);
      if (stored !== null) {
        const parsed: unknown = JSON.parse(stored);
        if (isValid(parsed)) return parsed;
      }
    } catch {
      /* unreadable storage — fall through to the default */
    }
    return fallback();
  });

  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* the choice still applies for this session */
      }
    },
    [key]
  );

  return [value, set];
};
